import { motion } from 'framer-motion';
import { Calendar, ExternalLink } from 'lucide-react';
import SectionHeading from '../components/SectionHeading';

interface NewsArticle {
  title: string;
  date: string;
  summary: string;
  url: string;
}

const newsArticles: NewsArticle[] = [
  {
    title: "PedigreePro adds weight recording and Weight & Profit Analysis",
    date: "October 13, 2026",
    summary: "PedigreePro now lets breeders record birth, weaning, slaughter and other weights for every animal, and weigh a whole group at once on a new shed weighing screen that works on a phone or tablet. Breeders can export lamb weights to Excel, and Premium subscribers also get a new Weight & Profit Analysis workbook, showing which ewes and rams are producing the best lambs. Importing weights directly from EID stick readers is planned for a later release.",
    url: "/what-it-does"
  },
  {
    title: "PedigreePro adds tupping records and calendar",
    date: "October 11, 2026",
    summary: "PedigreePro now includes a tupping data feature. Breeders can enter the tupping details for each individual ewe in the field and then output a tupping calendar, showing when each ewe is due to lamb. The feature is the latest in a run of autumn improvements to the free sheep management software.",
    url: "/what-it-does"
  },
  {
    title: "Sheep software uses AI for latest dynamic feature",
    date: "January 21, 2026",
    summary: "PedigreePro sheep management software has integrated artificial intelligence technology to streamline record-keeping. The new feature enables users to download pedigree documents as PDFs and import animal data directly into the system, automatically populating the pedigree tree. The AI advancement uses optical character recognition (OCR) technology to read multi-generation pedigrees from national sheep databases like Grassroots. Premium subscribers can now import up to 31 animals simultaneously, significantly reducing manual data entry.",
    url: "https://andyfrazier.wordpress.com/2026/01/21/sheep-software-uses-ai-for-latest-dynamic-feature/"
  },
  {
    title: "Podcaster and sheep breeder launches free sheep management software",
    date: "October 1, 2025",
    summary: "Andy Frazier, a podcaster and sheep breeder, has launched PedigreePro, free software designed for managing sheep flocks. The app operates on both mobile and web platforms with an intuitive interface for field or home use. Frazier drew on two decades of IT experience and extensive sheep knowledge to develop the program. The software offers data entry, spreadsheet reporting, and pedigree registration file generation, and is free for flocks under 25 sheep.",
    url: "https://andyfrazier.wordpress.com/2025/10/01/podcaster-and-sheep-breeder-launches-free-sheep-management-software/"
  }
];

export default function News() {
  return (
    <section className="py-20 px-6">
      <div className="max-w-4xl mx-auto">
        <SectionHeading
          title="News & Press Releases"
          subtitle="Latest updates and announcements from PedigreePro"
        />

        <div className="mt-12 space-y-8">
          {newsArticles.map((article, index) => (
            <motion.article
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-shadow"
            >
              <div className="flex items-center gap-2 text-gray-600 mb-3">
                <Calendar size={16} />
                <time className="text-sm">{article.date}</time>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {article.title}
              </h2>

              <p className="text-gray-700 leading-relaxed mb-6">
                {article.summary}
              </p>

              <a
                href={article.url}
                {...(article.url.startsWith('/') ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
                className="inline-flex items-center gap-2 text-green-700 hover:text-green-800 font-medium transition-colors"
              >
                Read full article
                {!article.url.startsWith('/') && <ExternalLink size={16} />}
              </a>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
